import { Injectable } from '@angular/core';
import * as cutBankOutActions from './cutbank-out-db.actions';
import * as cutBankOutSelectors from './cutbank-out-db.selectors';

import { select, Store } from '@ngrx/store';

import { AppNotificationUtilClass, InputAppDataService } from '@monorepo/services';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import { TInputAppState } from '../reducer';
import {
  TREQUEST_BULK_EDIT_COMPONENT_TAG_QTY,
  TREQUEST_BULK_SAVE_CUTBANK_OUT,
  TREQUEST_BUNDLE_TAG_DETAILS,
  TREQUEST_COMPONENT_TAG_VALDATION_CUTBANK_OUT,
  TREQUEST_CUTBANK_OUT_ACTIVITY_LOG,
  TREQUEST_CUTBANK_OUT_TRANSACTION_LOG,
  TREQUEST_DELETE_TRANSACTION_LOG,
  TREQUEST_GET_COMPONENT_BUNDLE_TAG,
  TREQUEST_GET_COMPONENT_TAG_BY_RFID,
  TREQUEST_GET_CUT_TAG,
  TREQUEST_RFID_TAG_VALIDATION,
  TREQUEST_LINE_IN_STORE_TAG_STATUS_CUTBANK_OUT,
  TREQUEST_TAG_VALIDATION_CUTBANK_OUT,
  TREQUEST_UNDO_TAG_VALIDATION_CUTBANK_OUT,
  TREQUEST_UNDO_TAG_VALIDATION_WITH_BCTX,
  TREQUEST_UNDO_TRANSACTION_LINE_IN_EVENT_GENERATE,
  TREQUEST_VALIDATE_AND_UPDATED_RFID_TAGS_INFO,
} from '@monorepo/types';
import { from, lastValueFrom } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export const TRANSACTION_LOG_LISTING_CUTBANK_OUT_ITEMS_PER_PAGE_LIST = [8, 10, 50, 100, 200];
export const ACTIVITY_LOG_LISTING_CUTBANK_OUT_ITEMS_PER_PAGE_LIST = [8, 10, 50, 100, 200];

@Injectable({ providedIn: 'root' })
export class CutbankOutFacade {
  constructor(
    private inputAppDataService: InputAppDataService,
    private appMasterDBFacade: AppMasterDBFacade,
    private store: Store<TInputAppState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private translate: TranslateService,
  ) {}

  fetchSubjects() {
    this.store.dispatch(new cutBankOutActions.GetSubjects());
  }

  getSubjects() {
    return this.store.pipe(select(cutBankOutSelectors.getSubjects));
  }

  fetchComponentBundleScanEnabled() {
    this.store.dispatch(new cutBankOutActions.IsComponentBundleScanEnabled());
  }

  getComponentScanEnabled() {
    return this.store.pipe(select(cutBankOutSelectors.IsComponentBundleScanEnabled));
  }

  async getCutTagValidation(payload: TREQUEST_TAG_VALIDATION_CUTBANK_OUT) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.validateTagCutbankOut(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  validateLineInStoreTagStatus(payload: TREQUEST_LINE_IN_STORE_TAG_STATUS_CUTBANK_OUT) {
    return from(this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'))).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.validateLineInStoreTagStatusCutbankOut(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  async getCutTagDetailsById(payload: TREQUEST_GET_CUT_TAG) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.getCutBankInCuttag(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async getComponentTagValidation(payload: TREQUEST_COMPONENT_TAG_VALDATION_CUTBANK_OUT) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.validateComponentBundleTagCutbankOut(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  getComponentBundleTagDetailsById(payload: TREQUEST_GET_COMPONENT_BUNDLE_TAG) {
    return from(this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'))).pipe(
      map((loaderEl) => {
        return loaderEl;
      }),
      switchMap((loaderEl) => {
        return this.inputAppDataService.getCutbankComponentTag(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  async bulkSaveTransactions(payload: TREQUEST_BULK_SAVE_CUTBANK_OUT) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.bulkSaveCutbankTransaction(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async bulkSaveComponentTagTransactions(payload: TREQUEST_BULK_SAVE_CUTBANK_OUT) {
    const loading = await this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'));

    return lastValueFrom(this.inputAppDataService.bulkSaveCutbankComponentTagTransaction(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  getBundleTagDetails(payload: TREQUEST_BUNDLE_TAG_DETAILS) {
    return from(this.appNotificationUtilClass.presentLoading(this.translate.instant('common.loading'))).pipe(
      map((loaderEl) => {
        return loaderEl;
      }),
      switchMap((loaderEl) => {
        return this.inputAppDataService.getBundleTagDetails(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  bulkEditComponentTagQuantities(payload: TREQUEST_BULK_EDIT_COMPONENT_TAG_QTY) {
    return from(this.appNotificationUtilClass.presentLoading()).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.bulkEditComponentTagQty(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  fetchTransactionLogInfo(payload: TREQUEST_CUTBANK_OUT_TRANSACTION_LOG) {
    return this.store.dispatch(new cutBankOutActions.GetTransactionLog(payload));
  }

  fetchActivityLogInfo(payload: TREQUEST_CUTBANK_OUT_ACTIVITY_LOG) {
    return this.store.dispatch(new cutBankOutActions.GetActivityLog(payload));
  }

  getTransactionLog() {
    return this.store.pipe(select(cutBankOutSelectors.getTransactionLogInfo));
  }

  getActivityLog() {
    return this.store.pipe(select(cutBankOutSelectors.getActivityLogInfo));
  }

  updateRfIdTagsInfo(payload: TREQUEST_VALIDATE_AND_UPDATED_RFID_TAGS_INFO) {
    return this.inputAppDataService.updateRfIdTagsInfo(payload);
  }

  validateRfId(payload: TREQUEST_RFID_TAG_VALIDATION) {
    return this.inputAppDataService.validateRfIdTag(payload);
  }

  undoTransactionLog(payload: TREQUEST_UNDO_TAG_VALIDATION_CUTBANK_OUT) {
    return from(
      this.appNotificationUtilClass.presentLoading(this.translate.instant('cutbank_common.undo_transaction')),
    ).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.getCutbankTransactionUndoValidation(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  undoWithBctxValidation(payload: TREQUEST_UNDO_TAG_VALIDATION_WITH_BCTX) {
    return from(
      this.appNotificationUtilClass.presentLoading(this.translate.instant('cutbank_common.bctx_validation')),
    ).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.cutbankTransactionUndoWithBctx(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  deleteTransactionLog(payload: TREQUEST_DELETE_TRANSACTION_LOG) {
    return from(
      this.appNotificationUtilClass.presentLoading(this.translate.instant('cutbank_common.delete_transaction_log')),
    ).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.deleteCutbankTransaction(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  lineInUndoLog(payload: TREQUEST_UNDO_TRANSACTION_LINE_IN_EVENT_GENERATE) {
    return from(
      this.appNotificationUtilClass.presentLoading(this.translate.instant('cutbank_common.line_in_undo_log')),
    ).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.lineInUndoEvent(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }

  getTagInfoFromScannedRfidTags(payload: TREQUEST_GET_COMPONENT_TAG_BY_RFID) {
    return from(this.appNotificationUtilClass.presentLoading()).pipe(
      switchMap((loaderEl) => {
        return this.inputAppDataService.getComponentTagByRfid(payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
        );
      }),
    );
  }
}

export type TTransactionLogCutbankOutInfo = ReturnType<CutbankOutFacade['getTransactionLog']>;
export type TActivityLogCutBankOutInfo = ReturnType<CutbankOutFacade['getActivityLog']>;
